import { serviceTypeBadge } from '@/components/features/offerings/common'
import { DataTable } from '@/components/features/shared/DataTable'
import { SummaryExtended } from '@/components/features/shared/SummaryExtended'
import { PriceLabel, PriceLabelSimple, renderDatetime } from '@/components/features/shared/utils'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  Agreement,
  AgreementStatus,
  formatAgreementStatus,
  getAgreementStatusColor,
} from '@/domain/agreements/agreement'
import { UserRole } from '@/domain/auth/auth'
import { formatBillingFrequencyShort, ServiceType } from '@/domain/offerings/offering'
import { useAgreements, useAgreementStats } from '@/hooks/api/useAgreements'
import { useOfferings } from '@/hooks/api/useOfferings'
import { useServerPagination } from '@/hooks/useServerPagination'
import { Paths } from '@/routes/paths'
import { useAuthStore } from '@/store/useAuthStore'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { addMonths, endOfMonth, format, startOfMonth, subMonths } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  ArrowRight,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  FileText,
  Loader2,
  Plus,
  Search
} from 'lucide-react'
import { PageContainer } from '@/components/features/shared/PageContainer'
import { NavHeader } from '@/components/features/shared/NavHeader'

export const AgreementsManagement = () => {
  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as any
  const { hasAnyRole } = useAuthStore()

  const statusFilter = searchParams.status || 'all'
  const serviceTypeFilter = searchParams.serviceType || 'all'
  const monthParam = searchParams.month // YYYY-MM
  const showByMonth = !!monthParam

  const selectedMonth = monthParam ? new Date(monthParam + '-01T12:00:00') : new Date()

  const {
    pagination,
    setPagination: handlePaginationChange,
    search,
    inputValue,
    setInputValue,
    handleSearch,
    updateQueryParams,
    limit,
  } = useServerPagination({
    clearDefaults: {
      status: 'all',
      serviceType: 'all',
    },
    resetOn: [statusFilter, serviceTypeFilter],
  })

  const { data: offerings } = useOfferings()
  const { data: agreements, isLoading } = useAgreements({
    search,
    status: statusFilter === 'all' ? undefined : statusFilter as AgreementStatus,
    serviceType: serviceTypeFilter === 'all' ? undefined : serviceTypeFilter,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    dateFrom: showByMonth ? format(startOfMonth(selectedMonth), 'yyyy-MM-dd') : undefined,
    dateTo: showByMonth ? format(endOfMonth(selectedMonth), 'yyyy-MM-dd') : undefined,
    sortBy: 'c.updated_at',
    sortOrder: 'DESC',
  })

  // console.log(agreements)

  const { data: stats } = useAgreementStats({
    search,
    status: statusFilter === 'all' ? undefined : statusFilter as AgreementStatus,
    serviceType: serviceTypeFilter === 'all' ? undefined : serviceTypeFilter,
    dateFrom: showByMonth ? format(startOfMonth(selectedMonth), 'yyyy-MM-dd') : undefined,
    dateTo: showByMonth ? format(endOfMonth(selectedMonth), 'yyyy-MM-dd') : undefined,
    sortBy: 'c.updated_at',
    sortOrder: 'DESC',
  })

  const canEdit = hasAnyRole([UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler])

  const handleEdit = (agreementId: string) => {
    navigate({
      to: `${Paths.Management.Contracts}/${agreementId}`,
    })
  }


  const handlePrevMonth = () => {
    const newMonth = subMonths(selectedMonth, 1)
    updateQueryParams({ month: format(newMonth, 'yyyy-MM'), page: 1 })
  }

  const handleNextMonth = () => {
    const newMonth = addMonths(selectedMonth, 1)
    updateQueryParams({ month: format(newMonth, 'yyyy-MM'), page: 1 })
  }

  const handleCurrentMonth = () => {
    updateQueryParams({ month: format(new Date(), 'yyyy-MM') })
  }

  const columns: ColumnDef<Agreement>[] = [
    {
      accessorKey: 'service',
      header: 'Servicio',
      cell: ({ row }) => {
        const agreement = row.original
        const offering = offerings?.find((o) => o.id === agreement.offeringId)
        return (
          <div className="flex flex-col gap-1">
            {offering && <span>{serviceTypeBadge(offering.serviceType)}</span>}
            <span className="text-sm">{offering?.code || ''} - {agreement.offeringName}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'client',
      header: 'Cliente',
      cell: ({ row }) => {
        const agreement = row.original
        return (
          <div className="font-medium whitespace-nowrap flex flex-col">
            {agreement.userName}
            {agreement.contractNumber && (
              <span className="text-sm text-muted-foreground">
                {agreement.contractNumber}
              </span>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => {
        const agreement = row.original
        return (
          <Badge className={getAgreementStatusColor(agreement.status)}>
            {formatAgreementStatus(agreement.status)}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'startDate',
      header: 'Inicio',
      cell: ({ row }) => {
        const agreement = row.original
        return (
          <span className="whitespace-nowrap">
            {format(new Date(agreement.startDate), 'dd/MM/yyyy', { locale: es })}
          </span>
        )
      },
    },
    {
      accessorKey: 'endDate',
      header: 'Fin',
      cell: ({ row }) => {
        const agreement = row.original
        return (
          <span className="whitespace-nowrap">
            {agreement.endDate
              ? format(new Date(agreement.endDate), 'dd/MM/yyyy', { locale: es })
              : '-'}
          </span>
        )
      },
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      cell: ({ row }) => {
        const agreement = row.original
        return (
          (agreement.status === AgreementStatus.Active || agreement.status === AgreementStatus.Pending) ?
            <div className="flex items-center gap-1">
              <PriceLabel price={agreement.amount!} />
              <span className='font-mono'>{formatBillingFrequencyShort(agreement.billingFrequency)}</span>
            </div>
            : <span className="text-muted-foreground">-</span>
        )
      },
    },
    {
      accessorKey: 'outstandingAmount',
      header: 'Saldo',
      cell: ({ row }) => {
        const agreement = row.original
        return (
          !agreement.outstandingAmount ? <span className="text-muted-foreground">-</span> :
            <div className={`col-span-1 font-bold w-[max-content] ${agreement.outstandingAmount! < 0 ? 'text-red-600' : 'text-green-600'}`}>
              <PriceLabelSimple price={agreement.outstandingAmount!} />
            </div>
        )
      },
    },
    {
      accessorKey: 'updatedAt',
      header: 'Ult. modificación',
      cell: ({ row }) => {
        const agreement = row.original
        return renderDatetime(agreement.updatedAt ?? null)
      },
    },
    {
      id: 'actions',
      header: 'Acciones',
      cell: ({ row }) => {
        const agreement = row.original
        return (
          <div className="text-right">
            <IconButton
              title='Editar contrato'
              onClick={() => handleEdit(agreement.id)}
            >
              <ArrowRight className="h-3.5 w-3.5" />
            </IconButton>
          </div>
        )
      },
    },
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <PageContainer>
      <NavHeader hideBackButton
        actions={
          canEdit && (
            <Button onClick={() => navigate({ to: `${Paths.Management.NewContract}` })}>
              <Plus className="h-4 w-4 mr-2" />
              Nueva Contratación
            </Button>
          )
        }
      >
        <h1 className="text-3xl font-bold tracking-tight">Contrataciones</h1>
        <p className="text-muted-foreground">
          Gestiona las contrataciones de cualquier servicio
        </p>
      </NavHeader>

      <div className="flex flex-col gap-4">
        {/* Month Navigation */}
        <div className="flex items-center justify-between bg-muted/30 p-4 rounded-lg border">
          <div className="flex items-center space-x-2">
            <Switch
              id="show-by-month"
              checked={showByMonth}
              onCheckedChange={(checked) => {
                updateQueryParams({
                  month: checked ? (monthParam || format(new Date(), 'yyyy-MM')) : undefined,
                  page: 1
                })
              }}
            />
            <Label htmlFor="show-by-month">Ver contratos por mes</Label>
          </div>

          {showByMonth && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-[150px] text-center font-medium capitalize">
                {format(selectedMonth, 'MMMM yyyy', { locale: es })}
              </div>
              <Button variant="outline" size="sm" onClick={handleNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleCurrentMonth} className="ml-2">
                Mes actual
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-end gap-4">
        <div className="flex flex-col gap-y-2 w-full max-w-sm">
          <Label>Búsqueda</Label>
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por cliente o contrato..."
              className="pl-8"
              autoFocus
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-y-2">
          <Label>Estado</Label>
          <Select value={statusFilter} onValueChange={(val) => {
            updateQueryParams({ status: val })
          }}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value={AgreementStatus.Pending}>Pendiente</SelectItem>
              <SelectItem value={AgreementStatus.Active}>Activo</SelectItem>
              <SelectItem value={AgreementStatus.Suspended}>Suspendido</SelectItem>
              <SelectItem value={AgreementStatus.Terminated}>Completado</SelectItem>
              {/* <SelectItem value={AgreementStatus.Expired}>Expirado</SelectItem> */}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-y-2">
          <Label>Tipo de servicio</Label>
          <Select value={serviceTypeFilter} onValueChange={(val) => {
            updateQueryParams({ serviceType: val })
          }}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Tipo de servicio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los servicios</SelectItem>
              <SelectItem value={ServiceType.ABONOS}>Abonos</SelectItem>
              <SelectItem value={ServiceType.CREMACIONES}>Cremaciones</SelectItem>
              <SelectItem value={ServiceType.PARCELAS}>Parcelas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button className='ml-auto' onClick={handleSearch} variant="info">Buscar</Button>
      </div>

      <h2 className='font-semibold text-xl'>Resultados de la búsqueda</h2>
      <SummaryExtended
        items={[
          {
            title: 'Total Contrataciones',
            value: stats?.total || 0,
            icon: FileText,
            subtitle: 'Contrataciones registradas',
            valueClassName: 'text-2xl font-bold',
          },
          {
            title: 'Activas',
            value: stats?.active || 0,
            icon: CheckCircle,
            subtitle: 'En ejecución',
            valueClassName: 'text-2xl font-bold',
          },
          {
            title: 'Completadas',
            value: stats?.completed || 0,
            icon: CheckCircle,
            subtitle: 'Finalizadas con éxito',
            valueClassName: 'text-2xl font-bold',
          },
          {
            title: 'Total Deuda',
            value: `$${stats?.totalOutstanding?.toLocaleString('es-AR') || 0}`,
            icon: DollarSign,
            subtitle: 'Suma de saldos pendientes',
            valueClassName: 'text-2xl font-bold',
          },
        ]}
      />

      <Card>
        <CardHeader className='pb-2'>
          <CardTitle>Lista de Contrataciones</CardTitle>
          <CardDescription>
            Contrataciones encontradas para los filtros seleccionados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={agreements?.data || []}
            pageCount={agreements?.totalPages || -1}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            totalRecords={agreements?.total}
            onRowClick={(agreement) => handleEdit(agreement.id)}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
